import React, {useState, useEffect} from 'react';
import {Link, useLocation} from 'react-router-dom';
import {motion, AnimatePresence} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiMenu, FiX, FiChevronDown, FiHeart} = FiIcons;

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();

  const navigation = [
    {name: 'Home', path: '/'},
    {name: 'About', path: '/about', dropdown: [
      {name: 'Our Story', path: '/about/story'},
      {name: 'Leadership', path: '/about/leadership'},
      {name: 'Annual Reports', path: '/about/reports'}
    ]},
    {name: 'Our Work', path: '/our-work', dropdown: [
      {name: 'Rehabilitation', path: '/our-work/rehabilitation'},
      {name: 'Skill Training', path: '/our-work/training'},
      {name: 'Healthcare', path: '/our-work/healthcare'}
    ]},
    {name: 'Impact', path: '/impact'},
    {name: 'Get Involved', path: '/get-involved', dropdown: [
      {name: 'Donate', path: '/crowdfunding'},
      {name: 'Volunteer', path: '/get-involved/volunteer'},
      {name: 'Fundraise', path: '/get-involved/fundraise'}
    ]},
    {name: 'CSR', path: '/csr'},
    {name: 'Contact', path: '/contact'}
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleDropdownToggle = (index) => {
    setActiveDropdown(activeDropdown === index ? null : index);
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled 
        ? 'bg-white/95 backdrop-blur-md shadow-premium border-b border-gray-100' 
        : 'bg-white/80 backdrop-blur-sm'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Premium Logo with Actual Logo Image */}
          <Link to="/" className="flex items-center space-x-4 group">
            <motion.div 
              whileHover={{scale: 1.05}} 
              className="relative"
            >
              {/* Logo Image */}
              <div className="w-16 h-16 rounded-2xl overflow-hidden shadow-lg group-hover:shadow-premium transition-all duration-300 bg-white">
                <img 
                  src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1759495915116-Begging%20%20Free%20India.png"
                  alt="Begging Free India Foundation Logo"
                  className="w-full h-full object-contain p-1"
                />
              </div>
              {/* Active Status Indicator */}
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></div>
            </motion.div>
            
            <div className="hidden sm:block">
              <div className="typography-heading text-xl text-gray-900">Begging Free India</div>
              <div className="text-xs text-gray-600 font-medium">Foundation</div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navigation.map((item, index) => (
              <div key={item.name} className="relative">
                {item.dropdown ? (
                  <div className="group">
                    <button
                      onClick={() => handleDropdownToggle(index)}
                      className={`flex items-center space-x-1 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                        location.pathname.startsWith(item.path)
                          ? 'text-orange-600 bg-orange-50'
                          : 'text-gray-700 hover:text-orange-600 hover:bg-gray-50'
                      }`}
                    >
                      <span>{item.name}</span>
                      <SafeIcon icon={FiChevronDown} className="w-4 h-4" />
                    </button>
                    
                    <AnimatePresence>
                      {activeDropdown === index && (
                        <motion.div
                          initial={{opacity: 0, y: 10}}
                          animate={{opacity: 1, y: 0}}
                          exit={{opacity: 0, y: 10}}
                          className="absolute top-full left-0 mt-2 w-56 bg-white rounded-xl shadow-premium border border-gray-100 py-2"
                        >
                          {item.dropdown.map((dropItem) => (
                            <Link
                              key={dropItem.name}
                              to={dropItem.path}
                              className="block px-4 py-3 text-sm text-gray-700 hover:text-orange-600 hover:bg-gray-50 transition-colors duration-200"
                              onClick={() => setActiveDropdown(null)}
                            >
                              {dropItem.name}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <Link
                    to={item.path}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                      location.pathname === item.path
                        ? 'text-orange-600 bg-orange-50'
                        : 'text-gray-700 hover:text-orange-600 hover:bg-gray-50'
                    }`}
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center space-x-4">
            <motion.button
              whileHover={{scale: 1.05}}
              whileTap={{scale: 0.95}}
              className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-full hover:border-gray-300 transition-all duration-200"
            >
              Volunteer
            </motion.button>
            
            <Link to="/crowdfunding">
              <motion.button
                whileHover={{scale: 1.05}}
                whileTap={{scale: 0.95}}
                className="btn-premium-primary text-sm"
              >
                Donate Now
              </motion.button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-2 text-gray-700 hover:text-orange-600 transition-colors duration-200"
          >
            <SafeIcon icon={isMenuOpen ? FiX : FiMenu} className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{opacity: 0, height: 0}}
            animate={{opacity: 1, height: 'auto'}}
            exit={{opacity: 0, height: 0}}
            className="lg:hidden bg-white border-t border-gray-100"
          >
            <div className="max-w-7xl mx-auto px-4 py-4 space-y-2">
              {navigation.map((item) => (
                <div key={item.name}>
                  <Link
                    to={item.path}
                    onClick={() => setIsMenuOpen(false)}
                    className={`block px-4 py-3 text-base font-medium rounded-lg transition-colors duration-200 ${
                      location.pathname === item.path
                        ? 'text-orange-600 bg-orange-50'
                        : 'text-gray-700 hover:text-orange-600 hover:bg-gray-50'
                    }`}
                  >
                    {item.name}
                  </Link>
                </div>
              ))}
              
              <div className="pt-4 space-y-3">
                <button className="w-full px-6 py-3 text-sm font-medium text-gray-700 bg-gray-100 rounded-full">
                  Volunteer
                </button>
                <Link to="/crowdfunding" onClick={() => setIsMenuOpen(false)}>
                  <button className="w-full btn-premium-primary text-sm">
                    Donate Now
                  </button>
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;